import React from 'react';

/**
 * MarkdownLite — safe text renderer for our 2 hard-coded markdown features :
 *   - **bold**       → <strong>
 *   - newline `\n`   → <br />
 *
 * Note (07/02/2026 review) : this component is intentionally JSX-only.
 * It does NOT use raw HTML injection. All consumers read from hardcoded
 * `data/*.js` files (never user input), but we keep this implementation
 * audit-friendly by rendering through React's normal escape path.
 *
 * Tokenization is intentionally minimal — we do NOT support links, lists,
 * code blocks or any other markdown feature. Anything else is rendered
 * as plain text and escaped by React's normal JSX path.
 *
 * 28/05/2026 v2 — Code review fix (XSS hardening — JSX only, no raw HTML).
 */

const BOLD_RE = /\*\*(.+?)\*\*/g;

function renderInline(text, strongClassName, keyPrefix = '') {
  // Tokenize **bold** spans and emit a mix of strings & <strong> nodes.
  const nodes = [];
  let lastIndex = 0;
  let m;
  let i = 0;
  // Reset the regex's lastIndex so consecutive calls behave consistently.
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      nodes.push(text.slice(lastIndex, m.index));
    }
    nodes.push(
      // 18/06/2026 — fix React duplicate key warning : le compteur `i`
      // repartait à 0 à chaque ligne → collisions `b-0` sur les textes
      // multi-lignes avec plusieurs passages en gras.
      <strong key={`b-${keyPrefix}${i++}`} className={strongClassName}>
        {m[1]}
      </strong>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export default function MarkdownLite({
  text,
  as: Tag = 'p',
  className,
  strongClassName = 'text-foreground font-semibold',
  testId,
  ...rest
}) {
  if (text == null || text === '') return null;
  // Split on newlines, render inline tokens per line, separate by <br />.
  const lines = String(text).split('\n');
  const children = [];
  for (let i = 0; i < lines.length; i++) {
    children.push(...renderInline(lines[i], strongClassName, `l${i}-`));
    if (i < lines.length - 1) {
      children.push(<br key={`br-${i}`} />);
    }
  }
  return (
    <Tag className={className} data-testid={testId} {...rest}>
      {children}
    </Tag>
  );
}
